import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  useSEO({ robots: decideRobots({ pageType: "auth" }), ...buildPageMeta("auth") });
  const { refreshSession } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const token = params.get("token") || "";

  /* eslint-disable */
  useEffect(() => {
    if (!token) { setState("error"); setError(t("verify.no_token")); return; }
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async () => {
        setState("ok");
        if (refreshSession) await refreshSession();
      })
      .catch((err) => {
        setState("error");
        setError(formatApiError(err.response?.data?.detail) || err.message);
      });
  }, [token, t, refreshSession]);
  /* eslint-enable */

  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="verify-email-page">
      {state === "loading" && (
        <>
          <Loader2 size={40} className="mx-auto text-[#5B8C66] animate-spin" strokeWidth={1.6}/>
          <h1 className="mt-4 text-2xl font-bold">{t("verify.checking")}</h1>
        </>
      )}
      {state === "ok" && (
        <div data-testid="verify-success">
          <CheckCircle2 size={48} className="mx-auto text-[#5B8C66]" strokeWidth={1.4}/>
          <h1 className="mt-4 text-3xl font-black tracking-tight">{t("verify.success_title")}</h1>
          <p className="mt-2 text-sm text-[#5F6B61]">{t("verify.success_body")}</p>
          <Link to="/" className="btn-pill mt-6 inline-block px-5 py-2.5 bg-[#5B8C66] text-white font-semibold">{t("verify.go_home")}</Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-error">
          <AlertTriangle size={40} className="mx-auto text-[#8E4A2E]" strokeWidth={1.4}/>
          <h1 className="mt-4 text-2xl font-bold">{t("verify.error_title")}</h1>
          <p className="mt-2 text-sm text-[#5F6B61]">{error}</p>
          <Link to="/" className="mt-4 inline-block text-[#5B8C66] font-bold">{t("verify.go_home")}</Link>
        </div>
      )}
    </div>
  );
}
